import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Trash2, Loader2, ArrowRight, Pencil, Check, X } from "lucide-react";
import { labelForGrade } from "@/lib/constants";
import { toast } from "sonner";

export default function History() {
  const [convs, setConvs] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConvs(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setConvs([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const del = async (id) => {
    if (!window.confirm("Bu sohbeti silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/chat/conversations/${id}`);
      setConvs((c) => c.filter((x) => x.id !== id));
      toast.success("Sohbet silindi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditTitle(c.title || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const saveEdit = async (id) => {
    const title = editTitle.trim();
    if (!title) {
      toast.error("Başlık boş olamaz");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/chat/conversations/${id}`, { title });
      setConvs((c) => c.map((x) => (x.id === id ? { ...x, title: data.title || title } : x)));
      toast.success("Başlık güncellendi");
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell variant="student">
      <div className="max-w-4xl mx-auto p-5 lg:p-10" data-testid="history-page">
        <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Geçmiş</div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
          Sohbet Geçmişin
        </h1>
        <p className="mt-2 text-zinc-600">Önceki sohbetlerine geri dön, başlığı düzenle veya sil.</p>

        <div className="mt-8">
          {convs === null && (
            <div className="text-sm text-zinc-500 py-10 text-center">
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            </div>
          )}
          {convs && convs.length === 0 && (
            <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center" data-testid="history-empty">
              <MessageSquare className="w-6 h-6 text-zinc-400 mx-auto" />
              <div className="mt-3 font-display text-lg font-semibold text-zinc-900">
                Henüz sohbet yok
              </div>
              <p className="mt-1 text-sm text-zinc-600">İlk sorunla başla.</p>
              <Link to="/panel/sohbet" className="inline-block mt-5">
                <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                  Sohbete Başla
                </Button>
              </Link>
            </div>
          )}
          {convs && convs.length > 0 && (
            <ul className="space-y-2">
              {convs.map((c) => {
                const isEditing = editingId === c.id;
                return (
                  <li
                    key={c.id}
                    className="rounded-2xl border border-zinc-200 bg-white p-4 flex items-center gap-4"
                    data-testid={`conv-item-${c.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0">
                      <MessageSquare className="w-4 h-4 text-[#1E3A8A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            ref={inputRef}
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit(c.id);
                              if (e.key === "Escape") cancelEdit();
                            }}
                            maxLength={100}
                            className="h-8 text-sm rounded-lg"
                            data-testid={`conv-edit-input-${c.id}`}
                          />
                          <button
                            onClick={() => saveEdit(c.id)}
                            disabled={saving}
                            className="p-1.5 rounded-lg text-[#1E3A8A] hover:bg-[#1E3A8A]/10 disabled:opacity-50"
                            data-testid={`conv-edit-save-${c.id}`}
                            aria-label="Kaydet"
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100"
                            data-testid={`conv-edit-cancel-${c.id}`}
                            aria-label="İptal"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-zinc-900 truncate" data-testid={`conv-title-${c.id}`}>{c.title}</div>
                            <button
                              onClick={() => startEdit(c)}
                              className="p-1 rounded text-zinc-400 hover:text-zinc-700 shrink-0"
                              data-testid={`conv-edit-${c.id}`}
                              aria-label="Yeniden adlandır"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {labelForGrade(c.grade_level)} · {c.course} ·{" "}
                            {new Date(c.updated_at).toLocaleString("tr-TR")}
                          </div>
                        </>
                      )}
                    </div>
                    {!isEditing && (
                      <>
                        <Link to={`/panel/sohbet?id=${c.id}`}>
                          <Button variant="outline" size="sm" className="rounded-full" data-testid={`conv-open-${c.id}`}>
                            Aç <ArrowRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </Link>
                        <button
                          onClick={() => del(c.id)}
                          className="p-2 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`conv-delete-${c.id}`}
                          aria-label="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
